import { cssVar } from '@mfw/common';
import type { Theme } from '@mui/material/styles';

import { circularProgressClasses } from '@mui/material/CircularProgress';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import { formControlClasses } from '@mui/material/FormControl';
import { iconButtonClasses } from '@mui/material/IconButton';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { buttonClasses } from '@mui/material/Button';
import { paperClasses } from '@mui/material/Paper';
import { listClasses } from '@mui/material/List';
import { alpha } from '@mui/material/styles';

import { paper } from '../../css';

// ----------------------------------------------------------------------

export function dataGrid(theme: Theme) {
  const paperStyles = paper({ dropdown: true, theme });

  return {
    MuiDataGrid: {
      styleOverrides: {
        // Row, Cell
        cell: {
          '&--editing': {
            backgroundColor: `rgba(${cssVar.primary.mainChannel} / 0.08) !important`,
            boxShadow: 'none !important',
          },
          borderBottom: `1px dashed`,
        },
        columnHeader: {
          '&--sorted': {
            color: cssVar.text.primary,
          },
          backgroundColor: cssVar.background.neutral,
          color: cssVar.text.secondary,
          fontSize: 14,
        },
        // Column
        columnHeaders: {
          borderBottom: 0,
        },
        columnSeparator: {
          color: theme.palette.divider,
        },
        columnsPanelRow: {
          margin: theme.spacing(0.5, 0),
        },
        filterForm: {
          alignItems: 'center',
          gap: theme.spacing(1.5),
          padding: theme.spacing(1),
        },
        filterFormValueInput: {
          [`& .${formControlClasses.root}`]: {
            width: '100%',
          },
          [`& .${inputLabelClasses.root}`]: {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
        // Footer
        footerContainer: {
          borderTop: `1px dashed`,
          minHeight: 'auto',
        },
        iconButtonContainer: {
          [`& .${iconButtonClasses.root}`]: {
            marginLeft: theme.spacing(1),
            padding: theme.spacing(0.25),
          },
        },
        menu: {
          [`& .${listClasses.root}`]: {
            [`& .${listItemIconClasses.root}`]: {
              marginRight: theme.spacing(2),
              minWidth: 0,
            },
            padding: 0,
          },
          [`& .${paperClasses.root}`]: {
            ...paperStyles,
            minWidth: 140,
          },
        },
        // Icons
        menuIcon: {
          [`& .${iconButtonClasses.root}`]: {
            margin: theme.spacing(0, 1),
            padding: theme.spacing(0.25),
          },
        },
        overlay: {
          [`& .${circularProgressClasses.root}`]: {
            color: cssVar.text.primary,
          },
        },
        panelContent: {
          padding: theme.spacing(1),
        },
        panelFooter: {
          [`& .${buttonClasses.root}`]: {
            '&:first-of-type': {
              border: `solid 1px ${alpha(theme.palette.grey[500], 0.24)}`,
            },
            '&:last-of-type': {
              backgroundColor: cssVar.text.primary,
              color: cssVar.background.paper,
            },
            padding: theme.spacing(0.5, 1.5),
          },
          borderTop: `dashed 1px ${theme.palette.divider}`,
          display: 'none',
          gap: theme.spacing(1),
          justifyContent: 'flex-end',
          padding: theme.spacing(2),
        },
        // Columns Panel
        panelHeader: {
          padding: theme.spacing(2, 2, 0, 2),
        },
        // Paper
        paper: {
          ...paperStyles,
          padding: 0,
        },
        root: {
          '--unstable_DataGrid-headWeight': theme.typography.fontWeightSemiBold,
          '--unstable_DataGrid-radius': 0,
          borderWidth: 0,
        },
        selectedRowCount: {
          display: 'none',
          whiteSpace: 'nowrap',
        },
        // Toolbar
        toolbarContainer: {
          gap: theme.spacing(2),
          padding: theme.spacing(2),
        },
        toolbarQuickFilter: {
          padding: 0,
          [theme.breakpoints.up('md')]: {
            width: 'unset',
          },
          width: '100%',
        },
        withBorderColor: {
          borderColor: theme.palette.divider,
        },
      },
    },
  };
}
